// Utilitaire pour parser le fichier CSV des tickets

/**
 * Parser une valeur CSV en millimes (entier) sans erreur de virgule flottante
 * "38,000" -> 38000, "15,833" -> 15833, "-44,000" -> -44000
 */
const parseToMillimes = (value) => {
  if (!value || value === '-' || value === '') return 0;
  const str = value.toString().trim().replace(/^"|"$/g, '').replace(/\s/g, '');
  const isNegative = str.startsWith('-');
  const cleaned = isNegative ? str.substring(1) : str;
  const [intPart = '0', decPart = '000'] = cleaned.split(',');
  const intVal = parseInt(intPart, 10) || 0;
  const decVal = parseInt((decPart + '000').slice(0, 3), 10) || 0; // 3 décimales
  const millimes = intVal * 1000 + decVal;
  return isNegative ? -millimes : millimes;
};

// Convertir millimes ↔ nombre (exportées pour les pages)
export const toMillimes = (num) => Math.round((num || 0) * 1000);
export const fromMillimes = (m) => (m || 0) / 1000;

/**
 * Parser une ligne CSV
 */
const parseCSVLine = (line) => {
  const parts = [];
  let current = '';
  let inQuotes = false;
  
  for (let i = 0; i < line.length; i++) {
    const char = line[i];
    
    if (char === '"') {
      inQuotes = !inQuotes;
    } else if (char === ',' && !inQuotes) {
      // Supprimer les guillemets et espaces
      parts.push(current.trim().replace(/^"|"$/g, ''));
      current = '';
    } else {
      current += char;
    }
  }
  // Supprimer les guillemets du dernier élément
  parts.push(current.trim().replace(/^"|"$/g, ''));
  
  return parts;
};

/**
 * Parser le fichier CSV complet
 */
export const parseTicketCSV = (csvContent) => {
  const lines = csvContent.split('\n').filter(line => line.trim());
  const tickets = [];
  
  for (let i = 0; i < lines.length; i++) {
    const parts = parseCSVLine(lines[i]);
    
    if (parts.length >= 11) {
      const prixUnitaireRaw = parts[7]?.trim() || '';
      const totalRaw = parts[9]?.trim() || '';
      const ticket = {
        numTicket: parts[0]?.trim() || '',
        date: parts[1]?.trim() || '',
        heure: parts[2]?.trim() || '',
        numTable: parts[3]?.trim() || '',
        nbrCouverts: parseInt(parts[4]?.trim() || '0') || 0,
        vendeur: parts[5]?.trim() || '',
        article: parts[6]?.trim() || '',
        prixUnitaire: fromMillimes(parseToMillimes(prixUnitaireRaw)),
        prixUnitaireMillimes: parseToMillimes(prixUnitaireRaw),
        quantite: parseInt(parts[8]?.trim() || '1') || 1,
        total: fromMillimes(parseToMillimes(totalRaw)),
        totalMillimes: parseToMillimes(totalRaw),
        methodePaiement: parts[10]?.trim() || '-'
      };
      
      // Ignorer les lignes avec articles vides, certains articles spéciaux, ou prix 0 (notes)
      const article = ticket.article?.trim() || '';
      const shouldIgnore = !article || 
                          article === '' || 
                          article === 'PRIMO' || 
                          article === 'SEGUITO' || 
                          article === 'A POINT' ||
                          ticket.prixUnitaireMillimes === 0; // Prix 0 = note, pas un plat
      
      if (!shouldIgnore) {
        tickets.push(ticket);
      }
    }
  }
  
  return tickets;
};

/**
 * Convertir une date au format DD/MM/YYYY en objet Date
 */
const parseDateString = (dateStr) => {
  if (!dateStr) return null;
  const parts = dateStr.split('/');
  if (parts.length !== 3) return null;
  
  const day = parseInt(parts[0], 10);
  const month = parseInt(parts[1], 10) - 1; // mois 0-indexed
  const year = parseInt(parts[2], 10);
  
  if (isNaN(day) || isNaN(month) || isNaN(year)) return null;
  
  return new Date(year, month, day);
};

/**
 * Convertir une date YYYY-MM-DD (input date) en format DD/MM/YYYY
 */
const formatDateForExclusion = (dateStr) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};

/**
 * Filtrer les tickets par plage de dates et dates exclues
 */
export const filterTicketsByDate = (tickets, startDate, endDate, excludedDates = []) => {
  if (!tickets || tickets.length === 0) return tickets;
  
  return tickets.filter(ticket => {
    if (!ticket.date) return false;
    
    // Convertir la date du ticket (format DD/MM/YYYY) en objet Date
    const ticketDate = parseDateString(ticket.date);
    if (!ticketDate) return false;
    
    // Vérifier si la date est dans la plage
    if (startDate) {
      const start = new Date(startDate);
      start.setHours(0, 0, 0, 0);
      if (ticketDate < start) return false;
    }
    
    if (endDate) {
      const end = new Date(endDate);
      end.setHours(23, 59, 59, 999);
      if (ticketDate > end) return false;
    }
    
    // Vérifier si la date est exclue (convertir les dates exclues au format DD/MM/YYYY)
    const dateStr = ticket.date;
    const excludedFormatted = excludedDates.map(d => {
      // Si c'est déjà au format DD/MM/YYYY, l'utiliser tel quel
      if (d.includes('/')) return d;
      // Sinon, convertir depuis YYYY-MM-DD
      return formatDateForExclusion(d);
    });
    
    if (excludedFormatted.includes(dateStr)) return false;
    
    return true;
  });
};


/**
 * Calculer les statistiques depuis les tickets
 * Les montants sont additionnés comme dans le CSV (arithmétique entière pour des totaux exacts)
 */
export const calculateStatistics = (tickets) => {
  if (!tickets || tickets.length === 0) {
    return {
      totalRevenue: 0,
      totalTickets: 0,
      totalItems: 0,
      mostSoldPlate: null,
      leastSoldPlate: null,
      revenueByDay: {},
      revenueByMonth: {},
      paymentMethods: {},
      topPlates: [],
      topTables: [],
      averageTicketValue: 0
    };
  }
  
  // Revenus totaux - addition exacte des valeurs du CSV (totalMillimes = parse direct sans float)
  const totalRevenueMillimes = tickets.reduce((sum, ticket) => sum + (ticket.totalMillimes ?? toMillimes(ticket.total)), 0);
  const totalRevenue = fromMillimes(totalRevenueMillimes);
  
  // Nombre de tickets uniques
  const uniqueTickets = new Set(tickets.map(t => t.numTicket));
  const totalTickets = uniqueTickets.size;
  
  // Articles les plus/moins vendus
  const plateCounts = {};
  const plateRevenue = {};
  
  tickets.forEach(ticket => {
    if (ticket.article && ticket.article !== '') {
      const article = ticket.article.trim();
      const m = ticket.totalMillimes ?? toMillimes(ticket.total);
      plateCounts[article] = (plateCounts[article] || 0) + ticket.quantite;
      plateRevenue[article] = fromMillimes((plateRevenue[article] ? toMillimes(plateRevenue[article]) : 0) + m);
    }
  });
  
  // Filtrer les plats avec quantité nette positive pour les statistiques
  const validPlates = Object.entries(plateCounts).filter(([_, count]) => count > 0);
  
  // Trouver le plat le plus vendu
  let mostSoldPlate = null;
  let maxCount = 0;
  validPlates.forEach(([article, count]) => {
    if (count > maxCount) {
      maxCount = count;
      mostSoldPlate = { article, count, revenue: plateRevenue[article] || 0 };
    }
  });
  
  // Trouver le plat le moins vendu (avec au moins 1 vente)
  let leastSoldPlate = null;
  let minCount = Infinity;
  validPlates.forEach(([article, count]) => {
    if (count < minCount && count > 0) {
      minCount = count;
      leastSoldPlate = { article, count, revenue: plateRevenue[article] || 0 };
    }
  });
  
  // Top 10 plats
  const topPlates = validPlates
    .map(([article, count]) => ({
      article,
      count,
      revenue: plateRevenue[article] || 0
    }))
    .sort((a, b) => b.count - a.count)
    .slice(0, 10);
  
  // Revenus par jour - addition exacte du CSV
  const revenueByDay = {};
  tickets.forEach(ticket => {
    if (ticket.date) {
      const m = ticket.totalMillimes ?? toMillimes(ticket.total);
      const prev = revenueByDay[ticket.date] ? toMillimes(revenueByDay[ticket.date]) : 0;
      revenueByDay[ticket.date] = fromMillimes(prev + m);
    }
  });
  
  // Revenus par mois - addition exacte du CSV
  const revenueByMonth = {};
  tickets.forEach(ticket => {
    if (ticket.date) {
      const dateParts = ticket.date.split('/');
      if (dateParts.length === 3) {
        const monthKey = `${dateParts[2]}-${dateParts[1]}`;
        const m = ticket.totalMillimes ?? toMillimes(ticket.total);
        const prev = revenueByMonth[monthKey] ? toMillimes(revenueByMonth[monthKey]) : 0;
        revenueByMonth[monthKey] = fromMillimes(prev + m);
      }
    }
  });
  
  // Méthodes de paiement - addition exacte du CSV
  const paymentMethods = {};
  tickets.forEach(ticket => {
    const method = ticket.methodePaiement && ticket.methodePaiement !== '-' 
      ? ticket.methodePaiement 
      : 'Non spécifié';
    const m = ticket.totalMillimes ?? toMillimes(ticket.total);
    const prev = paymentMethods[method] ? toMillimes(paymentMethods[method]) : 0;
    paymentMethods[method] = fromMillimes(prev + m);
  });
  
  // Tables les plus utilisées
  const tableUsage = {};
  tickets.forEach(ticket => {
    if (ticket.numTable && ticket.numTable !== '0') {
      tableUsage[ticket.numTable] = (tableUsage[ticket.numTable] || 0) + 1;
    }
  });
  
  const topTables = Object.entries(tableUsage)
    .map(([table, count]) => ({ table, count }))
    .sort((a, b) => b.count - a.count)
    .slice(0, 10);
  
  // Valeur par ticket - addition exacte du CSV
  const ticketTotalsMillimes = {};
  tickets.forEach(ticket => {
    const m = ticket.totalMillimes ?? toMillimes(ticket.total);
    ticketTotalsMillimes[ticket.numTicket] = (ticketTotalsMillimes[ticket.numTicket] || 0) + m;
  });
  const ticketValues = Object.values(ticketTotalsMillimes);
  const averageTicketValue = ticketValues.length > 0
    ? fromMillimes(Math.round(ticketValues.reduce((a, b) => a + b, 0) / ticketValues.length))
    : 0;
  
  return {
    totalRevenue,
    totalTickets,
    totalItems: tickets.length,
    mostSoldPlate,
    leastSoldPlate,
    revenueByDay,
    revenueByMonth,
    paymentMethods,
    topPlates,
    topTables,
    averageTicketValue,
    plateCounts,
    plateRevenue
  };
};

